import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    MenuItem,
    Grid,
    Alert,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../api/client';

interface PO {
    id: number;
    po_number: string;
    vendor_name: string;
    total_amount: number;
    status: string;
}

const ReceivingCreateModal: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const [pos, setPOs] = useState<PO[]>([]);
    const [selectedPO, setSelectedPO] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchAvailablePOs();
    }, []);

    const fetchAvailablePOs = async () => {
        try {
            const response = await apiClient.get('/purchase-orders/list', {
                params: {
                    status: 'sent_to_farm',
                    limit: 50
                }
            });
            console.log('Fetched POs:', response.data);
            setPOs(response.data.pos || []); // API returns 'pos' not 'po_list'
        } catch (err: any) {
            console.error('Failed to fetch POs:', err);
            setError(err.response?.data?.detail || 'Failed to fetch POs');
            setPOs([]); // Ensure pos is always an array
        }
    };

    const handleCreate = async () => {
        if (!selectedPO) return;

        try {
            setLoading(true);
            const response = await apiClient.post('/receiving/create-from-po', null, {
                params: { po_id: selectedPO }
            });

            // Navigate to the newly created receiving
            navigate(`/inward/receiving/${response.data.id}`);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to create receiving');
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        navigate('/inward/receiving');
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Receiving</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select a Purchase Order to start receiving goods. This will generate a new batch number.
                </Typography>

                <TextField
                    fullWidth
                    select
                    label="Select Purchase Order"
                    value={selectedPO || ''}
                    onChange={(e) => setSelectedPO(Number(e.target.value))}
                    disabled={loading}
                    sx={{ mt: 2 }}
                >
                    {pos.map((po) => (
                        <MenuItem key={po.id} value={po.id}>
                            {po.po_number} - {po.vendor_name} (Rs. {po.total_amount.toLocaleString()})
                        </MenuItem>
                    ))}
                </TextField>

                {selectedPO && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        A new batch number will be generated and linked to this receiving operation.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    disabled={!selectedPO || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    Create Receiving
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReceivingCreateModal;
